import {ReactElement, ReactNode, useEffect, useMemo, useState} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import {Field, Input, Label} from "@zendeskgarden/react-forms";
import {Inline} from "@zendeskgarden/react-loaders";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import {Property} from "../../../../contexts/properties/logic";
import {
    useGetAffordableInPlaceRentOverridesLazyQuery,
    useUpsertAffordableInPlaceRentOverridesMutation,
    VersionType,
} from "../../../../__generated__/generated_types";
import * as css from "./css.module.scss";
import {toast} from "react-toastify";
import {round} from "../../../../utils/math";

export interface AffordableInPlaceRentSetupParams {
    property: Property;
    budgetYear: number;
    customLabelComponent?: ReactNode;
}

interface UnitTypeRow {
    unitTypeId: string;
    unitTypeName: string;
    averageResidentialRent: string;
    averageSubsidizedResidentialRent: string;
    averageSubsidy: string;
    subsidizedUnitPercentage: string;
}

function calculateActualsMonthAndYear(property: Property): {monthIndex: number; year: number} {
    if (property.reforecastStartMonthIndex === 0) {
        return {monthIndex: 11, year: property.budgetYear - 2};
    }
    return {monthIndex: property.reforecastStartMonthIndex - 1, year: property.budgetYear - 1};
}

export function AffordableInPlaceRentSetup(props: AffordableInPlaceRentSetupParams): ReactElement | null {
    const {monthIndex, year} = useMemo(() => calculateActualsMonthAndYear(props.property), [props.property]);

    const [fetchData, {data, loading}] = useGetAffordableInPlaceRentOverridesLazyQuery({
        fetchPolicy: "no-cache",
    });
    const [saveChanges, {loading: saving}] = useUpsertAffordableInPlaceRentOverridesMutation();

    const [rows, setRows] = useState<UnitTypeRow[]>([]);
    const [initialRows, setInitialRows] = useState<UnitTypeRow[]>([]);

    useEffect(() => {
        fetchData({
            variables: {
                propertyId: props.property.id,
                budgetYear: props.budgetYear,
                year: year,
                versionType: VersionType.Actuals,
            },
        });
    }, [props.property, props.budgetYear]);

    useEffect(() => {
        if (!data || loading) {
            return;
        }

        const unitTypes = data.listUnitTypes ?? [];
        const overrides = data.affordableInPlaceRentOverrides ?? [];
        const subsidizedPercentageOverrides = data.affordableSubsidizedUnitPercentageOverrides ?? [];

        const builtRows: UnitTypeRow[] = unitTypes.map(ut => {
            const override = overrides.find(o => o.unitTypeId === ut.id);
            const subsidizedPercentageOverride = subsidizedPercentageOverrides.find(o => o.unitTypeId === ut.id);
            let subsidizedPercentage = subsidizedPercentageOverride?.subsidizedUnitPercentage?.[monthIndex];
            if(typeof subsidizedPercentage === "number") {
                subsidizedPercentage *= 100;
            } else if(typeof subsidizedPercentage === "string") {
                let decimalPlaces = 0;
                const dotIndex = subsidizedPercentage.indexOf(".");
                if (dotIndex !== -1) {
                    decimalPlaces = subsidizedPercentage.length - dotIndex - 1;
                }
                subsidizedPercentage = parseFloat(subsidizedPercentage);
                // When this operation ( *= 100) is performed on a number like "0.2667" it will return 26,669999999999998
                // While what we expect is 26.67
                // So we need to round it to number of decimal places - 2 of the original number
                subsidizedPercentage *= 100;
                if(Number.isNaN(subsidizedPercentage)) {
                    subsidizedPercentage = undefined;
                } 
                else {
                    subsidizedPercentage = parseFloat(subsidizedPercentage.toFixed(Math.max(0, decimalPlaces - 2)));
                }
            }
            let averageResidentialRent = override?.averageResidentialRent?.[monthIndex]?.toString() ?? "";
            if(averageResidentialRent !== "") {
                averageResidentialRent = round(parseFloat(averageResidentialRent), 2).toFixed(2);
            }
            let averageSubsidizedResidentialRent = override?.averageSubsidizedResidentialRent?.[monthIndex]?.toString() ?? "";
            if(averageSubsidizedResidentialRent !== "") {
                averageSubsidizedResidentialRent = round(parseFloat(averageSubsidizedResidentialRent), 2).toFixed(2);
            }
            let averageSubsidy = override?.averageSubsidy?.[monthIndex]?.toString() ?? "";
            if(averageSubsidy !== "") {
                averageSubsidy = round(parseFloat(averageSubsidy), 2).toFixed(2);
            }
            return {
                unitTypeId: ut.id,
                unitTypeName: ut.name,
                averageResidentialRent: averageResidentialRent,
                averageSubsidizedResidentialRent: averageSubsidizedResidentialRent,
                averageSubsidy: averageSubsidy,
                subsidizedUnitPercentage: subsidizedPercentage?.toString() ?? "",
            };
        });
        const initial = builtRows.map(row => ({...row}));

        setRows(builtRows);
        setInitialRows(initial);
    }, [data, loading]);

    const changes = useMemo(() => {
        if (initialRows.length !== rows.length) {
            return [];
        }
        return rows.filter((row, idx) => {
            const initial = initialRows[idx];
            if(!initial) {
                return false;
            }

            return row.averageResidentialRent !== initial.averageResidentialRent
                || row.averageSubsidizedResidentialRent !== initial.averageSubsidizedResidentialRent
                || row.averageSubsidy !== initial.averageSubsidy
                || row.subsidizedUnitPercentage !== initial.subsidizedUnitPercentage;
        });
    }, [rows, initialRows]);

    function updateRow(unitTypeId: string, field: keyof Pick<UnitTypeRow, "averageResidentialRent" | "averageSubsidizedResidentialRent" | "averageSubsidy" | "subsidizedUnitPercentage">, value: string) {
        setRows(prev => prev.map(row =>
            row.unitTypeId === unitTypeId ? {...row, [field]: value} : row
        ));
    }

    function handleSave() {
        if (changes.length === 0) {
            return;
        }

        const inputData = changes.map(row => ({
            unitTypeId: row.unitTypeId,
            monthIndex: monthIndex,
            averageResidentialRent: row.averageResidentialRent !== "" ? row.averageResidentialRent : null,
            averageSubsidizedResidentialRent: row.averageSubsidizedResidentialRent !== "" ? row.averageSubsidizedResidentialRent : null,
            averageSubsidy: row.averageSubsidy !== "" ? row.averageSubsidy : null,
        }));

        const toPercentage = (value: string | null | undefined) => {
            if(value === null || value === undefined) {
                return null;
            }
            const val = parseFloat(value) / 100;
            if(Number.isNaN(val)) {
                return null;
            }
            return val.toString();
        };

        const subsidizedInputData = changes.map(row => {
            return {
                unitTypeId: row.unitTypeId,
                monthIndex: monthIndex,
                subsidizedUnitPercentage: row.subsidizedUnitPercentage !== "" ? toPercentage(row.subsidizedUnitPercentage) : null,
            };
        });

        saveChanges({
            variables: {
                propertyId: props.property.id,
                year: year,
                versionType: VersionType.Actuals,
                data: inputData,
                unitPercentageData: subsidizedInputData
            },
        }).then(res => {
            if (res.data?.upsertAffordableInPlaceRentOverrides) {
                toast.success("Affordable In-Place Rent overrides saved successfully");
            } else {
                toast.error("Failed to save Affordable In-Place Rent overrides");
            }
        }).catch(() => {
            toast.error("Failed to save Affordable In-Place Rent overrides");
        });
    }

    function handleReset() {
        setRows(initialRows);
    }

    return (
        <>
            <div className={css.blockHeader}>
                <div>{props.customLabelComponent}</div>
                <div className={css.headerActions}>
                    <Button onClick={handleSave} isPrimary disabled={changes.length === 0 || saving}>
                        {saving ? <Inline size={24} aria-label="saving" /> : "Save"}
                    </Button>
                    <Button onClick={handleReset} disabled={!changes}>
                        Reset
                    </Button>
                </div>
            </div>
            <Table className={css.table}>
                <Head>
                    <HeaderRow>
                        <HeaderCell>Unit Type</HeaderCell>
                        <HeaderCell>Percentage of Subsidized Units</HeaderCell>
                        <HeaderCell>Avg Non-Subsidized In-Place Rent</HeaderCell>
                        <HeaderCell>Avg Subsidized Tenant Portion</HeaderCell>
                        <HeaderCell>Avg Subsidy</HeaderCell>
                    </HeaderRow>
                </Head>
                <Body>
                    {rows.map(row => (
                        <Row key={row.unitTypeId}>
                            <Cell>{row.unitTypeName}</Cell>
                            <Cell>
                                <Field>
                                    <Label hidden>{row.unitTypeName} Percentage of Subsidized Units</Label>
                                    <Input
                                        value={row.subsidizedUnitPercentage}
                                        type="number"
                                        isCompact
                                        onChange={e => updateRow(row.unitTypeId, "subsidizedUnitPercentage", e.target.value)}
                                    />
                                </Field>
                            </Cell>
                            <Cell>
                                <Field>
                                    <Label hidden>{row.unitTypeName} Avg Affordable In-Place Rent</Label>
                                    <Input
                                        value={row.averageResidentialRent}
                                        type="number"
                                        isCompact
                                        onChange={e => updateRow(row.unitTypeId, "averageResidentialRent", e.target.value)}
                                    />
                                </Field>
                            </Cell>
                            <Cell>
                                <Field>
                                    <Label hidden>{row.unitTypeName} Avg Subsidized Tenant Portion</Label>
                                    <Input
                                        value={row.averageSubsidizedResidentialRent}
                                        type="number"
                                        isCompact
                                        onChange={e => updateRow(row.unitTypeId, "averageSubsidizedResidentialRent", e.target.value)}
                                    />
                                </Field>
                            </Cell>
                            <Cell>
                                <Field>
                                    <Label hidden>{row.unitTypeName} Avg Subsidy</Label>
                                    <Input
                                        value={row.averageSubsidy}
                                        type="number"
                                        isCompact
                                        onChange={e => updateRow(row.unitTypeId, "averageSubsidy", e.target.value)}
                                    />
                                </Field>
                            </Cell>
                        </Row>
                    ))}
                </Body>
            </Table>
        </>
    );
}
